// `configureWorkspaceData` — the injected-config seam (pilot design D3,
// spec `monorepo-workspace-data-core`). Packages are variant-agnostic and
// store-free (G1/F5): the app injects the reactive auth-token hook and
// coding's SCC access-context callbacks at bootstrap
// (`src/utils/workspace-data-wiring.ts`). The package never imports
// `@/store`, app modules, or coding feature code. Transport is NOT part of
// this seam — `data/api` reads the app's configured clients from the
// `@heidi/api/client` registry (E5, ARCFE-805). Precedents:
// `@heidi/api`'s injected `applyTracingHeaders` and `@heidi/capture`'s
// `configure` door.
//
// The user and organisation reads cross here too, by ruling 65: an ACTION is
// injected whole with its app reactions, a VALUE as a resolved value or a hook
// returning one. Read them through `./injected-identity`, never the app hook.
// Optional like the reaction ports above, and the reader throws NAMING the
// member — a partial test double fails by name rather than as `undefined`.

import type { QueryClient } from '@tanstack/react-query';

import type { MultiWorkspaceSelectionGate } from '../flags';

import type { UpdateUserRequest } from '../contracts';
import type {
  GetRolesResponse,
  JoinedOrganizationsResponse,
  OrganizationMembersView,
  OrganizationWithTeamsResponse,
} from '../contracts/types/organization-teams.types';
import type { UserResponse } from '../contracts/types/user-types';
import type { DictationLanguageFlags } from './query/useQueryUser';

/**
 * Minimal structural view of the scribe v2 axios client that `data/api`
 * pulls from the `@heidi/api/client` registry — kept structural so this
 * package's public fn signatures never name axios types (declaration
 * portability in the standalone build).
 */
export interface WorkspaceHttpClient {
  get<T>(
    url: string,
    config?: {
      params?: Record<string, unknown>;
      timeout?: number;
      // `explicitKindeToken` joined the structural view with `getUserByToken`:
      // that read pins the request to a token the caller already holds instead
      // of the default store token. Axios declares the field globally (see
      // `packages/api/src/client/types.ts`); naming it here keeps this
      // structural view assignable from the real instance.
      explicitKindeToken?: string;
    }
  ): Promise<{ data: T }>;
  post<T>(
    url: string,
    body?: unknown,
    config?: { timeout?: number }
  ): Promise<{ data: T }>;
  // patch/delete joined the structural view with the OT2a teams carve
  // (ARCFE-932) — same narrow axios-compatible shapes as get/post.
  // `signal` joined with the D1 user-write carve (ARCFE-1037): `updateUser`
  // forwards the caller's AbortSignal so an in-flight PATCH /user can be
  // cancelled (axios reads the same field; behaviour byte-identical).
  patch<T = unknown>(
    url: string,
    body?: unknown,
    config?: { timeout?: number; signal?: AbortSignal }
  ): Promise<{ data: T }>;
  // `put` joined at EN3 (ARCFE-997): the two entitlement-assignment writes are
  // create-or-update by verb (GRO-3640 — a POST 409s when the backfill already
  // wrote a row), so the carve needs PUT, not a "tidier" POST.
  put<T = unknown>(
    url: string,
    body?: unknown,
    config?: { timeout?: number }
  ): Promise<{ data: T }>;
  delete<T = unknown>(
    url: string,
    // `data` joined at EN3 (ARCFE-997): four entitlement deletes carry a
    // request BODY. This is axios's shape for it and the wire contract the
    // backend reads — a body-less delete would drop the payload silently.
    config?: { params?: Record<string, unknown>; timeout?: number; data?: unknown }
  ): Promise<{ data: T }>;
}

export interface WorkspaceDataConfig {
  /**
   * A React HOOK returning the current auth token — injected as a hook (not a
   * getter) because the entitlements query key includes the token and must
   * re-key reactively on token refresh (today: a Rematch `useSelector`).
   * Must be hook-rules-safe: unconditional, stable identity.
   */
  useAuthToken: () => string | null | undefined;
  /**
   * A React HOOK answering whether auth is mid-transient-failure (a token
   * refresh that rejected with the session still alive) — today the app's
   * `authTransientFailureAtom`. A hook, not a getter, because the value is
   * reactive: `useWorkspaceHydrationStatus` classifies a settled 401 as an
   * expiry only when it is false. Must be hook-rules-safe: unconditional,
   * stable identity.
   */
  useIsTransientAuthFailure: () => boolean;
  /**
   * A React HOOK returning the post-login workspace selector's gate — the
   * app's `useMultiWorkspaceSelectionGate`, which composes the domain flag
   * getter with the provider-readiness read a package cannot make (it reads
   * env). Injected as a hook because both reads are reactive: an unsettled
   * provider HOLDS `useWorkspaceHydrationStatus`, and the hold releases when
   * it settles. Must be hook-rules-safe: unconditional, stable identity.
   */
  useMultiWorkspaceSelectionGate: () => MultiWorkspaceSelectionGate;
  /**
   * A React HOOK answering whether the current route is `/join-team` — today
   * `useRouter().pathname`, which a package may not read. An invitee there is
   * auto-picked and switched by the accept flow, so
   * `useWorkspaceHydrationStatus` never holds them on the selector (mirrors
   * the boot writer in `AuthWrapper`). Must be hook-rules-safe: unconditional,
   * stable identity.
   */
  useIsJoinTeamRoute: () => boolean;
  /**
   * Awaited after every successful entitlements fetch, BEFORE the query
   * resolves — so whatever reacts is settled by the time consumers see the
   * data (the legacy inline-mint ordering, preserved). Domain-neutral by
   * design: workspace does not know what reacts. Today the app wires it to
   * coding's `ensureSccAccessContext` (MR-WORKSPACE-SCC-INVERSION,
   * ARCFE-986 — this port REPLACED the five-callback `sccAccess` injection).
   * CONTRACT: the reaction must not reject; the query resolves regardless of
   * reaction outcome, exactly as the legacy swallow did.
   */
  onEntitlementsResolved?: (
    entitlements: ReadonlyMap<string, unknown>
  ) => Promise<void> | void;
  /**
   * The app's queryClient singleton — module-scope cache commands
   * (`getCurrentUser`, `getCurrentUserSnippets`, `invalidateUserCapabilities`,
   * …) run against it, exactly as their legacy `@/utils/reactQueryClient`
   * import did (same instance either way). Coding's seam is the precedent
   * (IP2, ARCFE-956).
   */
  queryClient: QueryClient;
  /**
   * Fired inside `useQueryTeamDetails`'s `queryFn` catch on a TRANSPORT error,
   * where the legacy toast fired (ARCFE-921). Bound here rather than per
   * caller because a `queryFn` belongs to the QUERY, not to an observer: the
   * fetch adopts the options of whichever observer started it, so a per-caller
   * `onFetchError` port fires or does not fire by MOUNT ORDER. See
   * `useQueryTeamDetails.test.tsx` for the rule, both before and after.
   * CONTRACT: the reaction must not throw; the frozen undefined-swallow
   * resolves the query regardless.
   */
  onTeamDetailsFetchError?: (error: WorkspaceHttpError) => void;
  /**
   * Fired inside `useQueryUserForContext`'s `queryFn` the moment the `/user/`
   * record resolves and BEFORE the query returns — the slot the legacy inline
   * block occupied. Every effect that used to sit there is a comparison or a
   * projection over exactly the two records this carries, which is why one
   * port replaces four unrelated-looking legs: the three quota toasts compare
   * `previousUser.quota` against `user.quota`, the seen-modal sync projects
   * `user.seen_modals`, and the temp-user clear plus the chat migration read
   * `user.chats_migrated_count`.
   *
   * Bound here rather than per caller for the reason
   * {@link WorkspaceDataConfig.onTeamDetailsFetchError} records: a `queryFn`
   * belongs to the QUERY, not to an observer, so a per-caller port fires or
   * does not fire by MOUNT ORDER.
   *
   * CONTRACT, and it is deliberately NOT the swallow
   * {@link WorkspaceDataConfig.onEntitlementsResolved} promises: the legacy
   * block ran unguarded inside the `queryFn`'s own `try`, so a throw from it
   * failed the query and was surfaced by the catch below it. That is preserved
   * — the reaction is invoked synchronously and anything it throws propagates.
   */
  onCurrentUserFetched?: (context: CurrentUserFetchedContext) => void;
  /**
   * Fired inside `useQueryUserForContext`'s `queryFn` catch on a TRANSPORT
   * error that is NOT the org-access refusal, where the legacy destructive
   * toast fired. The rethrow is the package's and happens either way.
   */
  onCurrentUserFetchError?: (error: WorkspaceHttpError) => void;
  /**
   * Fired inside `useQueryUserForContext`'s `queryFn` catch when the transport
   * error is the SSO-group refusal, where the legacy hard redirect ran. The
   * app owns both halves of that reaction — the "am I already there?" route
   * check and the `window.location.replace` — because a package may not read
   * the route tree, and the replace is deliberately hard: its legacy comment
   * records that it exists to cancel pending Next router navigations, so it
   * must not become a soft push. The package rethrows regardless, and
   * `retry` refuses to retry this class.
   */
  onOrgAccessRequired?: () => void;
  /**
   * Mirrors `{ languageSettingsEnabled: isDictationLanguageSettingsEnabled(),
   * outputLanguageEnabled: isDictationOutputLanguageEnabled() }`. A THUNK read
   * at call time — capturing it at wiring would freeze a mid-session flip, and
   * the two getters below already take the flags as an argument.
   * First sweep reader: `src/features/dictation/dictationBubble/store/buildUserContext.ts`.
   */
  getDictationLanguageFlags?: () => DictationLanguageFlags;
  /**
   * Mirrors `useAtomValue(selectedReviewMemberAtom)`. A HOOK because the atom
   * is reactive; `useIsReviewingOtherUser` already takes the resolved id.
   * First sweep reader: `src/features/sessions/components/session-list/session-list-settings-menu/session-list-settings-menu.tsx`.
   */
  useSelectedReviewMemberId?: () => string | null | undefined;
  /**
   * Mirrors `useMutateUpdateUser` — the app DELEGATE injected whole, because
   * its reactions are what make it correct (the composite user invalidation,
   * the commerce billing invalidation, the desktop note-defaults bridge).
   * First sweep reader: `src/features/dictation/dictate-onboarding-flow/steps/about-you-step.tsx`.
   */
  useUpdateUser?: (options?: { enableInvalidation?: boolean }) => UpdateUserDelegate;
  /**
   * Mirrors `useMutateUpdateSettings` — the app DELEGATE injected whole, the
   * shape `configureCodingSettingsUi` already uses for this same hook: two
   * toasts, the analytics dispatch and four invalidations ride with it.
   * First sweep reader: `src/features/dictation/dictate-onboarding-flow/steps/key-selection-step.tsx`.
   */
  useUpdateUserSettings?: (options?: {
    showToast?: boolean;
  }) => UpdateUserSettingsDelegate;
  /**
   * Mirrors `useCombinedOrganizationInfo`, whole: it WRITES the selected org
   * id from an effect and returns two CASL callbacks, so a value view of it
   * would drop the write and freeze the callbacks.
   * First sweep reader: `src/features/growth/practice-invite-team-members/usePracticeInvite.ts`.
   */
  useOrganizationInfo?: () => OrganizationInfoDelegate;
  /**
   * Mirrors `invalidateCurrentUser` — the app COMPOSITE over `GET_USER` and
   * commerce's `GET_PRODUCTS`, which a foundational domain may not reimplement.
   * Consumer-facing republish only: no hook in this package calls it.
   * First sweep reader: `src/features/dictation/dictate-onboarding-flow/utils/dictateOnboardingPersistence.ts`.
   */
  invalidateCurrentUser?: () => Promise<void>;
}

/**
 * What {@link WorkspaceDataConfig.useMultiWorkspaceSelectionGate} returns.
 *
 * DECLARED ON `../flags` and republished here, so this door's surface is
 * unchanged for every `/data` importer: the app flags barrel needs the type
 * too, and it may bind this package through `./flags` alone.
 */
export type { MultiWorkspaceSelectionGate };

/**
 * What both delegates below resolve to — the transport response of `PATCH
 * /user/`, structurally, so a consumer reading `result.data.email` typechecks
 * without this package naming axios.
 */
export interface UpdateUserResult {
  data: UserResponse;
}

/**
 * The per-call callbacks react-query's `mutate` takes as its second argument,
 * narrowed to what a caller may supply. `mutateAsync` has no need of them.
 */
export interface UpdateUserMutateOptions {
  onSuccess?: (result: UpdateUserResult, update: UpdateUserRequest) => void;
  onError?: (error: unknown, update: UpdateUserRequest) => void;
  onSettled?: () => void;
}

/**
 * The app `useMutateUpdateUser` delegate's surface, named structurally so this
 * package's public signatures never name react-query's mutation generics.
 */
export interface UpdateUserDelegate {
  mutate: (update: UpdateUserRequest, options?: UpdateUserMutateOptions) => void;
  mutateAsync: (update: UpdateUserRequest) => Promise<UpdateUserResult>;
  isPending: boolean;
}

/**
 * The app `useMutateUpdateSettings` delegate's surface, same discipline.
 *
 * Its variables carry an OPTIONAL `signal`, unlike the delegate above: the
 * settings save forwards the caller's `AbortSignal` to `updateUser`, so a form
 * unmounting mid-save cancels its own PATCH. Omitting it here would have made
 * that abort a compile error at three template-builder call sites and a
 * behavioural drop at any that dodged it.
 */
export interface UpdateUserSettingsDelegate {
  mutate: (
    update: UpdateUserRequest & { signal?: AbortSignal },
    options?: UpdateUserMutateOptions
  ) => void;
  mutateAsync: (
    update: UpdateUserRequest & { signal?: AbortSignal }
  ) => Promise<UpdateUserResult>;
  isPending: boolean;
}

/** What a CASL callback answers about one organisation member. */
export interface OrganizationMemberVerdict {
  canRemove: boolean;
  /**
   * The app's `MemberRemovalReason`, widened to its string values: a package
   * renders the reason, it does not branch on the app enum's identity.
   */
  reason: string | null;
}

/**
 * The app `useCombinedOrganizationInfo` delegate's surface — a three-query
 * join, so every payload here is the package's own contract type.
 */
export interface OrganizationInfoDelegate {
  isGlobalAdmin: boolean | undefined;
  joinedOrganizationData: JoinedOrganizationsResponse | null | undefined;
  isJoinedOrganizationFetched: boolean;
  organizationDetails: OrganizationWithTeamsResponse | null | undefined;
  isLoadingOrganizationDetails: boolean;
  isLoadingJoinedOrganization: boolean;
  isLoadingOrganization: boolean;
  orgRoles: GetRolesResponse['data'] | undefined;
  orgTeams: OrganizationWithTeamsResponse['organization']['teams'] | undefined;
  canMemberBeRemoved: (member: OrganizationMembersView) => OrganizationMemberVerdict;
  canMembersTeamBeChanged: (member: OrganizationMembersView) => OrganizationMemberVerdict;
  allowSessionLinkSharing: boolean;
  hasOrganization: boolean;
  isOrganizationTemplateManager: boolean;
}

/**
 * What {@link WorkspaceDataConfig.onCurrentUserFetched} carries. Both records
 * are the RAW `/user/` shape: the `hasUnlimitedQuota` derivation is applied to
 * the query's return value after the reaction runs, exactly as it was.
 */
export interface CurrentUserFetchedContext {
  /** The record `GET /user/` just returned. */
  user: UserResponse;
  /**
   * `queryClient.getQueryData([GET_USER])` read immediately before the
   * reaction — `undefined` on the first fetch of a session. The legacy block
   * read it through the same observer-scoped client and typed it as the raw
   * `UserResponse` even though the cache holds the derived shape; both are
   * preserved rather than tidied, because the three quota comparisons are
   * `undefined`-guarded on exactly this value.
   */
  previousUser: UserResponse | undefined;
}

let config: WorkspaceDataConfig | null = null;

export const configureWorkspaceData = (next: WorkspaceDataConfig): void => {
  config = next;
};

/** Fail loudly on unconfigured use — never silently degrade (spec scenario). */
export const getWorkspaceDataConfig = (): WorkspaceDataConfig => {
  if (!config) {
    throw new Error(
      '[workspace/data] not configured — app bootstrap must call configureWorkspaceData() ' +
        '(see src/utils/workspace-data-wiring.ts); tests exercising real workspace hooks ' +
        'must configure a test double first.'
    );
  }
  return config;
};

/**
 * Structural view of the transport error the app's axios client throws —
 * the same discipline as {@link WorkspaceHttpClient}: this package never
 * names axios types in its surface, so a consumer is not forced onto our
 * axios copy and the package needs no axios dependency of its own.
 *
 * `isAxiosError` is axios's own marker (what `axios.isAxiosError` reads), so
 * {@link isTransportError} is equivalent to the legacy `instanceof AxiosError`
 * checks these hooks replaced — and survives duplicate axios copies, which
 * `instanceof` does not.
 */
export interface WorkspaceHttpError {
  message: string;
  isAxiosError?: boolean;
  response?: { status?: number; data?: unknown };
}

/** True for an error axios produced (the `axios.isAxiosError` predicate). */
export const isTransportError = (error: unknown): error is WorkspaceHttpError =>
  typeof error === 'object' &&
  error !== null &&
  (error as { isAxiosError?: boolean }).isAxiosError === true;
